import { X, ExternalLink } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: () => void
}

/**
 * Instructions modal that fires before we open the store's Instagram post.
 *
 * Why a modal instead of a toast: the toast appears in the page the user is
 * leaving. Once the IG post opens (in a new tab or the IG app on mobile)
 * they're no longer looking at our page, so the toast goes unread. Showing
 * the steps before navigation is the only way to guarantee the user sees
 * them before doing the manual "Add to Story" action in Instagram.
 *
 * Phase 3 will add real automation via Web Share API with files (iOS/Android)
 * and the Instagram Stories URL scheme (Facebook App ID required). Until that
 * lands this manual flow is the only way to get IG Story shares working.
 */
export default function InstagramStoryHintModal({ open, onClose, onConfirm }: Props) {
  const { t } = useI18n()

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 px-5 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-card p-6 animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
          aria-label="close"
        >
          <X size={18} className="text-text-secondary" />
        </button>

        <h3 className="font-display font-semibold text-base text-text-primary mb-3">
          {t('share.instagramStoryTitle')}
        </h3>
        <ol className="space-y-2 font-body text-sm text-text-secondary">
          <li className="flex gap-2">
            <span className="font-semibold text-text-primary">1.</span>
            <span>{t('share.instagramStoryStep1')}</span>
          </li>
          <li className="flex gap-2">
            <span className="font-semibold text-text-primary">2.</span>
            <span>{t('share.instagramStoryStep2')}</span>
          </li>
          <li className="flex gap-2">
            <span className="font-semibold text-text-primary">3.</span>
            <span>{t('share.instagramStoryStep3')}</span>
          </li>
        </ol>

        <button
          onClick={onConfirm}
          className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 font-display text-sm font-semibold text-white tap-effect"
        >
          <ExternalLink size={16} />
          {t('share.instagramStoryOpen')}
        </button>
      </div>
    </div>
  )
}
